import React, { useContext, useEffect } from 'react';
import { FormikProps } from 'formik';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { Button, Grid, Typography } from '@material-ui/core';

import { ResearchSheet } from 'types';
import { ProofOfIdentityType } from 'enums';
import { FormItemTextField } from 'components/form-item/form-item-text-field';
import { FormItemSelectField } from 'components/form-item/form-item-select-field';
import { FormItemCheckboxGroupField } from 'components/form-item/form-item-checkbox-group-field';
import { I18nCtx } from 'components/i18n/i18n-context';
import { AuthCtx } from 'components/auth/auth-ctx';
import { getItemsByLocale } from 'components/i18n/locale/utils';
import { Loader } from 'components/loader/loader';
import { FormItemMultipleFileField } from 'components/form-item/form-item-multiple-file-field';
import { ConfirmationBadatelnaRulesCheckbox } from 'modules/confirmation-checkbox/confirmation-badatelna-rules-checkbox';
import { ConfirmationRemoteAccessCheckbox } from 'modules/confirmation-checkbox/confirmation-remote-access-checkbox';

const useStyles = makeStyles(() =>
  createStyles({
    adminNote: {
      fontWeight: 'bold',
    },
    container: {
      display: 'flex',
      justifyContent: 'center',
    },
    section: {
      marginTop: 50,
    },
    submitButton: {
      marginTop: 20,
    },
    submitText: {
      fontWeight: 'bold',
      color: 'white',
      textTransform: 'initial',
    },
  })
);

export function ResearchSheetBasic(props: FormikProps<ResearchSheet>) {
  const classes = useStyles();
  const { locale } = useContext(I18nCtx);
  const { userInfo, userLoaded } = useContext(AuthCtx);
  const {
    proofOfIdentityTypeOptions,
    researchCategoryOptions,
    researchSheetTypeOptions,
  } = getItemsByLocale(locale);
  const { values, setFieldValue } = props;
  const { id: sheetId, proofOfIdentityType, adminNote } = values;
  const casId = userInfo?.casId;

  useEffect(() => {
    if (proofOfIdentityType === ProofOfIdentityType.CAS) {
      setFieldValue('proofOfIdentityValue', casId);
    } else {
      setFieldValue('proofOfIdentityValue', '');
    }
  }, [casId, proofOfIdentityType, setFieldValue]);

  return (
    <form onSubmit={props.handleSubmit}>
      {props.isSubmitting && <Loader />}
      <Grid container className={classes.container}>
        {sheetId ? (
          <Grid item xs={12} md={8}>
            <Typography
              variant="body1"
              align="left"
              className={classes.section}
            >
              Přepracujte badatelský list dle poznámky správce:
            </Typography>
            <Typography
              variant="body1"
              align="left"
              className={classes.adminNote}
            >
              {adminNote}
            </Typography>
          </Grid>
        ) : (
          <Grid item xs={12} md={8}>
            <Typography
              variant="body1"
              align="left"
              className={classes.section}
            >
              Vyplňte údaje potřebné pro vytvoření Badatelského listu.
            </Typography>
          </Grid>
        )}
        <Grid item xs={12} md={8}>
          <FormItemSelectField
            name="proofOfIdentityType"
            label="Osobní doklad *"
            items={proofOfIdentityTypeOptions}
            disabled={userLoaded}
          />
        </Grid>
        <Grid item xs={12} md={8}>
          <FormItemTextField
            name="proofOfIdentityValue"
            label="Číslo dokladu *"
            disabled={proofOfIdentityType === ProofOfIdentityType.CAS}
          />
        </Grid>
        <Grid item xs={12} md={8}>
          <FormItemTextField name="researchTopic" label="Téma studia *" />
        </Grid>
        <Grid item xs={12} md={8}>
          <FormItemSelectField
            name="type"
            label="Účel nahlížení *"
            items={researchSheetTypeOptions}
          />
        </Grid>
        {props.values?.type === 'CREDENTIAL' && (
          <>
            <Grid item xs={12} md={8}>
              <FormItemMultipleFileField
                name="inspectionRequest"
                label="Přílohy žádosti"
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <FormItemTextField
                name="legalEntityName"
                label="Název právnické osoby *"
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <FormItemTextField
                name="legalEntityOffice.municipality"
                label={'Obec (adresa právnické osoby) *'}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <FormItemTextField
                name="legalEntityOffice.street"
                label={'Ulice (adresa právnické osoby) *'}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <FormItemTextField
                name="legalEntityOffice.zip"
                label={'PSČ (adresa právnické osoby) *'}
              />
            </Grid>
          </>
        )}
        <Grid item xs={12} md={8}>
          <ConfirmationRemoteAccessCheckbox name="remoteAccessAgreement" />
        </Grid>
        <Grid item xs={12} md={8}>
          <Typography variant="body1" align="left" className={classes.section}>
            Zaměření nahlížení *
          </Typography>
          <FormItemCheckboxGroupField
            name="researchCategories"
            items={researchCategoryOptions}
          />
        </Grid>
        {!sheetId && (
          <>
            <Grid item xs={12} md={8}>
              <FormItemTextField
                name="orderDescription"
                label="Popis objednávky"
                multiline={true}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <ConfirmationBadatelnaRulesCheckbox name="confirmation" />
            </Grid>
          </>
        )}
        <Grid item xs={12} md={8}>
          <Button
            className={classes.submitButton}
            variant="contained"
            color="primary"
            type="submit"
          >
            <Typography
              component="span"
              variant="body2"
              className={classes.submitText}
            >
              Odeslat žádost
            </Typography>
          </Button>
        </Grid>
      </Grid>
    </form>
  );
}
